import _ from 'lodash';
import styled from 'styled-components';
import { ReactNode, useRef, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';

import Field, { FieldType } from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import SettingsTable, {
	SettingsEmptyState,
} from 'packages/kakapo-ui-kit/components/common/SettingsTable';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

import SettingsItem from './SettingsItem';

const AnchorElement = styled.span`
	color: ${(props) => props.theme.colors.primary};
	cursor: pointer;
	text-decoration: underline;
`;

type QueryResult<K extends string, T> = {
	data?: Record<K, { edges: { node: Partial<T> }[] }>;
	refetch: () => Promise<any>;
};
type LazyQueryResult<K extends string, T> = [() => void, QueryResult<K, T>];

const SearchField = styled(Field)`
	border-radius: 20px;
	height: 40px;
`;

export type ArchiveRestoreMutationTuple = [
	(options: { variables: { input: { id: string } } }) => Promise<void>,
	{ loading: boolean },
];

export default function SettingsCollection<
	K extends string,
	V,
	T extends { id: string; isArchived: boolean },
>(props: {
	field: K;
	variables: V;
	query?: (options: { variables: V & { isArchived: boolean } }) => QueryResult<K, T>;
	lazyQuery: (options: { variables: V & { isArchived: boolean } }) => LazyQueryResult<K, T>;
	archive?: ArchiveRestoreMutationTuple;
	restore?: ArchiveRestoreMutationTuple;
	link: (item: T) => string;
	title: (item: T) => string;
	subheader?: ReactNode;
	translationKey: string;
	onAdd?: () => void;
}) {
	const { t } = useTranslation();

	const formik = useFormik({
		initialValues: { query: '' },
		onSubmit: () => {},
	});

	const [showArchived, setShowArchived] = useState(false);

	const queries = {
		active: props.query?.({ variables: { ...props.variables, isArchived: false, first: 1000 } }),
		archive: props.lazyQuery({
			variables: { ...props.variables, isArchived: true, first: 1000 },
		}),
	};

	const [cache, setCache] = useState<{ node: Partial<T> }[] | undefined>(undefined);

	const all =
		cache ??
		(queries.active?.data?.[props.field].edges ?? [])
			.concat(queries.archive[1]?.data?.[props.field].edges ?? [])
			.filter(({ node }) =>
				props
					.title(node as T)
					.toLocaleLowerCase()
					.includes(formik.values.query.toLocaleLowerCase()),
			);

	const ref = useRef(all);
	ref.current = all;

	const items = all?.filter(({ node }) => !node.isArchived);
	const sortedItems = items && _.sortBy(items, ({ node }) => props.title(node as T));
	const archivedItems = queries.archive[1]?.data && all?.filter(({ node }) => node.isArchived);
	const archivedSortedItems =
		archivedItems && _.sortBy(archivedItems, ({ node }) => props.title(node as T));

	const onRefresh = async () => {
		setCache(ref.current);
		await queries.active?.refetch();
		await queries.archive[1]?.refetch?.();
		setCache(undefined);
	};

	return (
		<>
			{props.query && (
				<Flex flexDirection="column" mb={4}>
					<SettingsTable
						title={t(`${props.translationKey}.title`)}
						description={t(`${props.translationKey}.description`)}
						toolbar={
							<>
								{props.onAdd && (
									<Button size={ButtonSize.Medium} onClick={props.onAdd}>
										{t('common.add')}
									</Button>
								)}
							</>
						}
						subheader={props.subheader}
					>
						<FormikProvider value={formik}>
							<Box mt={2} maxWidth={200}>
								<SearchField
									name="query"
									type={FieldType.Text}
									placeholder={t('common.search')}
									unspaced
									hideError
								/>
							</Box>
						</FormikProvider>
						{sortedItems ? (
							sortedItems.length > 0 ? (
								<>
									{sortedItems.map(({ node }) => (
										<SettingsItem
											key={node.id}
											title={props.title}
											link={props.link}
											item={node as T}
											archive={props.archive}
											restore={props.restore}
											onRefresh={onRefresh}
										/>
									))}
								</>
							) : (
								<SettingsEmptyState>{t(`${props.translationKey}.empty`)}</SettingsEmptyState>
							)
						) : (
							<Spinner isLoading />
						)}
					</SettingsTable>
				</Flex>
			)}

			{showArchived ? (
				<Flex flexDirection="column" mb={4}>
					<SettingsTable
						title={t(`${props.translationKey}.archived.title`)}
						description={t(`${props.translationKey}.archived.description`)}
					>
						{archivedSortedItems ? (
							archivedSortedItems.length > 0 ? (
								<>
									{archivedSortedItems.map(({ node }) => (
										<SettingsItem
											key={node.id}
											title={props.title}
											link={props.link}
											item={node as T}
											archive={props.archive}
											restore={props.restore}
											onRefresh={onRefresh}
										/>
									))}
								</>
							) : (
								<SettingsEmptyState>
									{t(`${props.translationKey}.archived.empty`)}
								</SettingsEmptyState>
							)
						) : (
							<Spinner isLoading />
						)}
					</SettingsTable>
				</Flex>
			) : (
				<Flex px={3}>
					<AnchorElement
						onClick={() => {
							setShowArchived(true);
							queries.archive[0]();
						}}
					>
						<ParagraphSmall>{t(`${props.translationKey}.archived.button.title`)}</ParagraphSmall>
					</AnchorElement>
				</Flex>
			)}
		</>
	);
}
